<template>
  <div class="feedback-list">
    <div class="toolbar">
      <h3>用户反馈</h3>
      <div class="toolbar-actions">
        <el-button
          size="small"
          type="primary"
          plain
          :disabled="!unreadCount"
          :loading="markingAll"
          @click="onMarkAllRead"
        >
          全部已读{{ unreadCount ? ` (${unreadCount})` : '' }}
        </el-button>
        <el-button size="small" @click="load">刷新</el-button>
      </div>
    </div>
    <el-table
      v-loading="loading"
      :data="items"
      stripe
      :row-class-name="rowClassName"
      @row-click="onRowClick"
    >
      <el-table-column label="状态" width="88" align="center">
        <template slot-scope="{ row }">
          <el-tag v-if="!row.read" size="mini" type="danger">未读</el-tag>
          <el-tag v-else size="mini" type="info">已读</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="时间" width="170">
        <template slot-scope="{ row }">{{ formatDateTime(row.createdAt) }}</template>
      </el-table-column>
      <el-table-column prop="userEmail" label="用户邮箱" min-width="160" show-overflow-tooltip />
      <el-table-column label="反馈内容" min-width="320">
        <template slot-scope="{ row }">
          <div class="feedback-cell">
            <p class="feedback-preview" :class="{ unread: !row.read }">{{ row.content }}</p>
            <el-button type="text" size="mini" @click.stop="openDetail(row)">查看全文</el-button>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="contact" label="联系方式" width="130" show-overflow-tooltip />
      <el-table-column label="页面" min-width="120">
        <template slot-scope="{ row }">
          <el-button v-if="row.pageUrl" type="text" size="mini" @click.stop="openDetail(row)">查看</el-button>
          <span v-else>-</span>
        </template>
      </el-table-column>
    </el-table>
    <div class="pager">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :current-page="page + 1"
        :page-size="size"
        :total="total"
        @current-change="onPageChange"
      />
    </div>

    <el-dialog title="反馈详情" :visible.sync="detailVisible" width="640px" @closed="onDetailClosed">
      <dl v-if="detail" class="detail-list">
        <dt>状态</dt>
        <dd>
          <el-tag v-if="!detail.read" size="mini" type="danger">未读</el-tag>
          <el-tag v-else size="mini" type="info">已读</el-tag>
        </dd>
        <dt>时间</dt>
        <dd>{{ formatDateTime(detail.createdAt) }}</dd>
        <dt>用户</dt>
        <dd>{{ detail.userEmail || '-' }}</dd>
        <dt>联系方式</dt>
        <dd>{{ detail.contact || '-' }}</dd>
        <dt>页面</dt>
        <dd class="detail-url">{{ detail.pageUrl || '-' }}</dd>
        <dt>内容</dt>
        <dd class="detail-content">{{ detail.content }}</dd>
      </dl>
      <span slot="footer">
        <el-button type="primary" @click="detailVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { fetchFeedback, markFeedbackRead, markAllFeedbackRead } from '../api/admin';
import { formatDateTime } from '../constants/planLabels';

export default {
  name: 'FeedbackList',
  data() {
    return {
      loading: false,
      markingAll: false,
      items: [],
      page: 0,
      size: 20,
      total: 0,
      unreadCount: 0,
      detailVisible: false,
      detail: null,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    formatDateTime,
    rowClassName({ row }) {
      return row.read ? '' : 'is-unread-row';
    },
    async load() {
      this.loading = true;
      try {
        const data = await fetchFeedback({ page: this.page, size: this.size });
        this.items = data.items || [];
        this.total = data.total || 0;
        this.unreadCount = data.unreadCount || 0;
        this.$emit('unread-change', this.unreadCount);
      } catch (e) {
        this.$message.error(e.message || '加载失败');
      } finally {
        this.loading = false;
      }
    },
    onPageChange(currentPage) {
      this.page = currentPage - 1;
      this.load();
    },
    onRowClick(row) {
      this.openDetail(row);
    },
    async openDetail(row) {
      this.detail = row;
      this.detailVisible = true;
      if (!row.read) {
        await this.markOneRead(row);
      }
    },
    async markOneRead(row) {
      try {
        const updated = await markFeedbackRead(row.id);
        this.applyReadState(row.id, updated);
        if (this.detail && this.detail.id === row.id) {
          this.detail = { ...this.detail, read: true, readAt: updated.readAt };
        }
      } catch (e) {
        this.$message.error(e.message || '标记已读失败');
      }
    },
    applyReadState(id, updated) {
      const idx = this.items.findIndex((item) => item.id === id);
      if (idx >= 0) {
        const wasUnread = !this.items[idx].read;
        this.$set(this.items, idx, {
          ...this.items[idx],
          read: true,
          readAt: updated && updated.readAt != null ? updated.readAt : this.items[idx].readAt,
        });
        if (wasUnread && this.unreadCount > 0) {
          this.unreadCount -= 1;
          this.$emit('unread-change', this.unreadCount);
        }
      }
    },
    async onMarkAllRead() {
      if (!this.unreadCount) return;
      this.markingAll = true;
      try {
        await markAllFeedbackRead();
        this.items = this.items.map((item) => ({ ...item, read: true }));
        this.unreadCount = 0;
        this.$emit('unread-change', 0);
        this.$message.success('已全部标记为已读');
      } catch (e) {
        this.$message.error(e.message || '全部已读失败');
      } finally {
        this.markingAll = false;
      }
    },
    onDetailClosed() {
      // 列表仍按未读优先；关闭详情后刷新当前页以纠正顺序
      if (this.items.some((item) => item.read) && this.page === 0) {
        this.load();
      }
    },
  },
};
</script>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.toolbar h3 { margin: 0; }
.toolbar-actions {
  display: flex;
  gap: 8px;
}
.pager { margin-top: 16px; text-align: right; }
.feedback-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}
.feedback-preview {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #374151;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  white-space: pre-wrap;
}
.feedback-preview.unread {
  color: #111827;
  font-weight: 600;
}
.detail-list {
  margin: 0;
}
.detail-list dt {
  font-weight: 600;
  color: #6b7280;
  font-size: 12px;
  margin-top: 12px;
}
.detail-list dt:first-child { margin-top: 0; }
.detail-list dd {
  margin: 4px 0 0;
  color: #111827;
  word-break: break-word;
}
.detail-url {
  font-size: 12px;
  color: #4b5563;
}
.detail-content {
  white-space: pre-wrap;
  line-height: 1.6;
  max-height: 360px;
  overflow-y: auto;
  padding: 10px 12px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}
</style>

<style>
.feedback-list .el-table .is-unread-row {
  cursor: pointer;
}
.feedback-list .el-table .is-unread-row td {
  background: #fff7ed !important;
}
</style>
